import { i18n } from '@/i18n';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

interface AlertButton<T = string> {
  propertyName: T;
  text: string;
  style: 'default' | 'secondary' | 'ghost' | 'danger' | 'link';
  needsValidation?: boolean;
}

interface SelectOption {
  value: string;
  label: string;
}

// Support both array format and object format for inputOptions
type InputOptions = SelectOption[] | Record<string, string>;

type AlertOptions<B extends string = string> = {
  titleText?: string;
  title?: string;
  text?: string;
  html?: string;
  icon?: 'success' | 'warning' | 'error' | 'info' | 'question';
  input?: 'text' | 'email' | 'password' | 'number' | 'select' | 'textarea' | 'checkbox';
  inputPlaceholder?: string;
  inputLabel?: string;
  inputValue?: string | boolean; // Initial value for the input
  inputOptions?: InputOptions; // For select input - supports both formats
  inputAttributes?: Record<string, any>; // Additional attributes for input components
  inputValidator?: (value: string) => string | false | null | undefined;
  showInputCopyButton?: boolean;
  allowOutsideClick?: boolean;
  allowEscapeKey?: boolean;
} & (LegacyAlertOptions | ModernAlertOptions<B>);

interface LegacyAlertOptions {
  // Legacy button system
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
  showDenyButton?: boolean;
  showCloseButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  denyButtonText?: string;
  focusDeny?: boolean;
  buttons?: never;
  // Don't include buttons property to prevent mixing
}

interface ModernAlertOptions<B extends string = string> {
  // Modern button system
  buttons: AlertButton<B>[];
  // Don't include legacy properties to prevent mixing
}

type AlertResult<T = string, B extends string = string> = {
  [K in B]: boolean;
} & {
  isConfirmed: boolean;
  isDenied: boolean;
  isDismissed: boolean;
  value: T;
};

export const useAlertStore = defineStore('alert', () => {
  const isVisible: Ref<boolean> = ref(false);
  const options: Ref<AlertOptions> = ref({});
  const resolveCallback: Ref<((result: any) => void) | null> = ref(null);
  const alertQueue: Ref<Array<{ options: AlertOptions; resolve: (result: any) => void }>> = ref([]);
  const isTransitioning: Ref<boolean> = ref(false);

  function showAlert<T = string, B extends string = string>(
    alertOptions: AlertOptions<B>,
  ): Promise<AlertResult<T, B>> {
    return new Promise((resolve) => {
      // Apply default values
      const defaultOptions: AlertOptions<B> = {
        allowOutsideClick: false,
        allowEscapeKey: false,
        ...alertOptions,
      };

      // Add to queue
      alertQueue.value.push({ options: defaultOptions, resolve });

      // Process queue if not already processing
      if (!isVisible.value && !isTransitioning.value) {
        processNextAlert();
      }
    });
  }

  const processNextAlert = () => {
    if (alertQueue.value.length === 0) return;

    const { options: alertOptions, resolve } = alertQueue.value.shift()!;

    // Check if it's a legacy or modern alert
    if ('buttons' in alertOptions && alertOptions.buttons) {
      // Modern alert with buttons array
      options.value = alertOptions;
    } else {
      // Legacy alert - convert to modern format
      const modernOptions: AlertOptions = {
        titleText: alertOptions.titleText,
        title: alertOptions.title,
        text: alertOptions.text,
        html: alertOptions.html,
        icon: alertOptions.icon,
        input: alertOptions.input,
        inputValue: alertOptions.inputValue,
        inputPlaceholder: alertOptions.inputPlaceholder,
        inputLabel: alertOptions.inputLabel,
        inputAttributes: alertOptions.inputAttributes,
        inputOptions: alertOptions.inputOptions,
        inputValidator: alertOptions.inputValidator,
        showInputCopyButton: alertOptions.showInputCopyButton,
        allowOutsideClick: alertOptions.allowOutsideClick,
        allowEscapeKey: alertOptions.allowEscapeKey,
        buttons: [],
      };

      // Apply legacy defaults
      const showConfirmButton = alertOptions.showConfirmButton ?? true;
      const showCancelButton = alertOptions.showCancelButton ?? false;
      const showDenyButton = alertOptions.showDenyButton ?? false;

      const { t } = i18n.global;

      if (showConfirmButton) {
        modernOptions.buttons.push({
          propertyName: 'isConfirmed',
          text: alertOptions.confirmButtonText || t('ok'),
          style: 'default',
          needsValidation: alertOptions.inputValidator !== undefined,
        });
      }

      if (showDenyButton) {
        modernOptions.buttons.push({
          propertyName: 'isDenied',
          text: alertOptions.denyButtonText || t('no'),
          style: 'danger',
        });
      }

      if (showCancelButton) {
        modernOptions.buttons.push({
          propertyName: 'isDismissed',
          text: alertOptions.cancelButtonText || t('cancel'),
          style: 'ghost',
        });
      }

      options.value = modernOptions;
    }

    resolveCallback.value = resolve;
    isVisible.value = true;
  };

  const resolveAlert = (result: any) => {
    if (resolveCallback.value) {
      resolveCallback.value(result);
      resolveCallback.value = null;
    }

    // Start transition
    isTransitioning.value = true;
    isVisible.value = false;

    // Wait for transition to complete, then process next alert
    setTimeout(() => {
      options.value = {};
      isTransitioning.value = false;

      // Process next alert in queue
      if (alertQueue.value.length > 0) {
        processNextAlert();
      }
    }, 300); // Match the transition duration
  };

  const hideAlert = () => {
    resolveAlert({
      dismissed: true,
    });
  };

  return {
    isVisible,
    options,
    showAlert,
    resolveAlert,
    hideAlert,
    alertQueue,
    isTransitioning,
  };
});

export type {
  AlertOptions,
  AlertResult,
  AlertButton,
  SelectOption,
  InputOptions,
  LegacyAlertOptions,
  ModernAlertOptions,
};
